/*
 * Kept in step with the backend's src/services/media/webmWriter.ts (WebM
 * transcripts send the same audio-only WebM); a fix in one belongs in the other.
 */
/**
 * A small audio-only WebM built from Opus or Vorbis packets: what a browser's
 * decodeAudioData reads when it has no WebCodecs for them, and what Whisper
 * is sent for a WebM's transcript.
 */

import type { XiphCodec } from './matroskaAudio';

const idBytes = (id: number): number[] => {
  const out: number[] = [];
  for (let value = id; value > 0; value = Math.floor(value / 256)) out.unshift(value & 0xff);
  return out;
};

/** An EBML size: always 8 bytes, which every reader accepts. */
const sizeBytes = (size: number): number[] => {
  const out = [0x01];
  for (let shift = 6; shift >= 0; shift -= 1) out.push(Math.floor(size / Math.pow(256, shift)) & 0xff);
  return out;
};

const element = (id: number, payload: Uint8Array | number[]): Uint8Array => {
  const head = idBytes(id).concat(sizeBytes(payload.length));
  const out = new Uint8Array(head.length + payload.length);
  out.set(head, 0);
  out.set(payload, head.length);
  return out;
};

const uintBytes = (value: number): number[] => {
  const out: number[] = [];
  for (let v = Math.max(0, Math.round(value)); out.length === 0 || v > 0; v = Math.floor(v / 256)) out.unshift(v & 0xff);
  return out;
};

const floatBytes = (value: number): number[] => {
  const view = new DataView(new ArrayBuffer(8));
  view.setFloat64(0, value);
  const out: number[] = [];
  for (let i = 0; i < 8; i += 1) out.push(view.getUint8(i));
  return out;
};

const ascii = (text: string): number[] => {
  const out: number[] = [];
  for (let i = 0; i < text.length; i += 1) out.push(text.charCodeAt(i) & 0x7f);
  return out;
};

const concatBytes = (parts: Uint8Array[]): Uint8Array => {
  let total = 0;
  for (let i = 0; i < parts.length; i += 1) total += parts[i].length;
  const out = new Uint8Array(total);
  let at = 0;
  for (let i = 0; i < parts.length; i += 1) {
    out.set(parts[i], at);
    at += parts[i].length;
  }
  return out;
};

/**
 * A WebM holding only these packets, timed from the first (in milliseconds),
 * a new cluster every 30 seconds so block times fit their 16-bit offsets.
 */
export const buildAudioWebm = (codec: XiphCodec, packets: Uint8Array[], timestampsUs: number[]): Uint8Array => {
  const header = element(0x1a45dfa3, concatBytes([
    element(0x4286, [1]),
    element(0x42f7, [1]),
    element(0x42f2, [4]),
    element(0x42f3, [8]),
    element(0x4282, ascii('webm')),
    element(0x4287, [4]),
    element(0x4285, [2]),
  ]));
  const info = element(0x1549a966, concatBytes([element(0x2ad7b1, uintBytes(1000000)), element(0x4d80, ascii('PithPlay'))]));
  const tracks = element(0x1654ae6b, element(0xae, concatBytes([
    element(0xd7, [1]),
    element(0x73c5, [1]),
    element(0x83, [2]),
    element(0x86, ascii(codec.kind === 'opus' ? 'A_OPUS' : 'A_VORBIS')),
    element(0x63a2, codec.description),
    element(0xe1, concatBytes([element(0xb5, floatBytes(codec.sampleRate)), element(0x9f, uintBytes(codec.channels))])),
  ])));
  const clusters: Uint8Array[] = [];
  const firstMs = timestampsUs.length ? timestampsUs[0] / 1000 : 0;
  let index = 0;
  while (index < packets.length) {
    const clusterMs = Math.max(0, Math.round(timestampsUs[index] / 1000 - firstMs));
    const blocks: Uint8Array[] = [element(0xe7, uintBytes(clusterMs))];
    for (; index < packets.length; index += 1) {
      const relative = Math.round(timestampsUs[index] / 1000 - firstMs) - clusterMs;
      if (relative > 30000) break;
      const block = new Uint8Array(4 + packets[index].length);
      block[0] = 0x81; // track 1
      block[1] = (relative >> 8) & 0xff;
      block[2] = relative & 0xff;
      block[3] = 0x80; // keyframe
      block.set(packets[index], 4);
      blocks.push(element(0xa3, block));
    }
    clusters.push(element(0x1f43b675, concatBytes(blocks)));
  }
  return concatBytes([header, element(0x18538067, concatBytes([info, tracks].concat(clusters)))]);
};
